import * as THREE from 'three';
import { GameMode, GameModeContext } from './GameMode';
import { TimeTrialHUD } from '../ui/TimeTrialHUD';
import { EventBus } from '../core/EventBus';

export interface Checkpoint {
  position: THREE.Vector3;
  radius: number;
  mesh?: THREE.Mesh;
  ringMesh?: THREE.Mesh;
}

export interface Track {
  name: string;
  checkpoints: Checkpoint[];
  laps: number;
  bestTime: number | null;
}

// Predefined tracks
const TRACKS: Track[] = [
  {
    name: 'City Circuit',
    checkpoints: [
      { position: new THREE.Vector3(0, 0, 30), radius: 8 },
      { position: new THREE.Vector3(50, 0, 50), radius: 8 },
      { position: new THREE.Vector3(80, 0, 0), radius: 8 },
      { position: new THREE.Vector3(50, 0, -50), radius: 8 },
      { position: new THREE.Vector3(0, 0, -30), radius: 8 },
      { position: new THREE.Vector3(-50, 0, -50), radius: 8 },
      { position: new THREE.Vector3(-80, 0, 0), radius: 8 },
      { position: new THREE.Vector3(-50, 0, 50), radius: 8 },
    ],
    laps: 3,
    bestTime: null,
  },
  {
    name: 'Sprint',
    checkpoints: [
      { position: new THREE.Vector3(0, 0, 20), radius: 8 },
      { position: new THREE.Vector3(30, 0, 40), radius: 8 },
      { position: new THREE.Vector3(60, 0, 20), radius: 8 },
      { position: new THREE.Vector3(90, 0, 40), radius: 8 },
      { position: new THREE.Vector3(120, 0, 20), radius: 8 },
    ],
    laps: 1,
    bestTime: null,
  },
  {
    name: 'Figure 8',
    checkpoints: [
      { position: new THREE.Vector3(0, 0, 0), radius: 10 },
      { position: new THREE.Vector3(40, 0, 40), radius: 8 },
      { position: new THREE.Vector3(80, 0, 0), radius: 8 },
      { position: new THREE.Vector3(40, 0, -40), radius: 8 },
      { position: new THREE.Vector3(0, 0, 0), radius: 10 },
      { position: new THREE.Vector3(-40, 0, -40), radius: 8 },
      { position: new THREE.Vector3(-80, 0, 0), radius: 8 },
      { position: new THREE.Vector3(-40, 0, 40), radius: 8 },
    ],
    laps: 2,
    bestTime: null,
  },
];

export class TimeTrialMode extends GameMode {
  private hud: TimeTrialHUD;
  private tracks: Track[] = [];
  private currentTrackIndex: number = 0;
  private currentCheckpoint: number = 0;
  private currentLap: number = 1;

  // Timing
  private raceStartTime: number = 0;
  private lapStartTime: number = 0;
  private lapTimes: number[] = [];
  private isRacing: boolean = false;
  private isFinished: boolean = false;
  private countdownTimer: number = 0;

  // Checkpoint visuals
  private checkpointMeshes: THREE.Group[] = [];

  // Ghost replay (simplified - just best lap time for now)
  private bestLapTime: number | null = null;

  constructor(context: GameModeContext) {
    super(context);
    this.hud = new TimeTrialHUD();
    this.loadTracks();
  }

  public getName(): string {
    return 'Time Trial';
  }

  private loadTracks(): void {
    // Load tracks and best times from localStorage
    this.tracks = TRACKS.map((track, index) => {
      const savedTime = localStorage.getItem(`timeTrial_${index}_bestTime`);
      return {
        ...track,
        checkpoints: track.checkpoints.map(cp => ({ ...cp })),
        bestTime: savedTime ? parseFloat(savedTime) : null,
      };
    });
  }

  protected onStart(): void {
    this.hud.show();
    this.selectTrack(0);
  }

  protected onStop(): void {
    this.hud.hide();
    this.clearCheckpointMeshes();
    this.isRacing = false;
    this.isFinished = false;
  }

  public selectTrack(index: number): void {
    if (index < 0 || index >= this.tracks.length) return;

    this.currentTrackIndex = index;
    this.currentCheckpoint = 0;
    this.currentLap = 1;
    this.lapTimes = [];
    this.isRacing = false;
    this.isFinished = false;

    const track = this.tracks[index];
    this.hud.updateTrackInfo(track.name, track.laps, track.bestTime);

    // Create checkpoint visuals
    this.createCheckpointMeshes();

    // Start countdown
    this.startCountdown();
  }

  private startCountdown(): void {
    this.countdownTimer = 3;
    this.hud.showCountdown(3);
  }

  private createCheckpointMeshes(): void {
    this.clearCheckpointMeshes();

    const track = this.tracks[this.currentTrackIndex];

    for (let i = 0; i < track.checkpoints.length; i++) {
      const cp = track.checkpoints[i];
      const group = new THREE.Group();

      // Checkpoint ring
      const ringGeom = new THREE.TorusGeometry(cp.radius, 0.3, 8, 32);
      const ringMat = new THREE.MeshStandardMaterial({
        color: i === 0 ? 0x00ff00 : 0x00ffff,
        emissive: i === 0 ? 0x00ff00 : 0x00ffff,
        emissiveIntensity: i === this.currentCheckpoint ? 1.5 : 0.3,
        transparent: true,
        opacity: 0.8,
      });
      const ring = new THREE.Mesh(ringGeom, ringMat);
      ring.rotation.x = Math.PI / 2;
      ring.position.y = 3;
      group.add(ring);

      // Checkpoint number
      // (Would need TextGeometry or sprite for proper numbers)

      // Vertical poles on sides
      const poleGeom = new THREE.CylinderGeometry(0.2, 0.2, 8, 8);
      const poleMat = new THREE.MeshStandardMaterial({
        color: 0x444444,
        metalness: 0.8,
        roughness: 0.2,
      });

      const leftPole = new THREE.Mesh(poleGeom, poleMat);
      leftPole.position.set(-cp.radius, 4, 0);
      group.add(leftPole);

      const rightPole = new THREE.Mesh(poleGeom, poleMat);
      rightPole.position.set(cp.radius, 4, 0);
      group.add(rightPole);

      // Glowing top beacons
      const beaconGeom = new THREE.SphereGeometry(0.4, 8, 8);
      const beaconMat = new THREE.MeshStandardMaterial({
        color: i === 0 ? 0x00ff00 : 0x00ffff,
        emissive: i === 0 ? 0x00ff00 : 0x00ffff,
        emissiveIntensity: 2,
      });

      const leftBeacon = new THREE.Mesh(beaconGeom, beaconMat);
      leftBeacon.position.set(-cp.radius, 8.2, 0);
      group.add(leftBeacon);

      const rightBeacon = new THREE.Mesh(beaconGeom, beaconMat);
      rightBeacon.position.set(cp.radius, 8.2, 0);
      group.add(rightBeacon);

      group.position.copy(cp.position);

      // Look at next checkpoint for proper orientation
      if (i < track.checkpoints.length - 1) {
        const nextPos = track.checkpoints[i + 1].position;
        group.lookAt(nextPos);
      } else if (track.laps > 1) {
        group.lookAt(track.checkpoints[0].position);
      }

      this.context.scene.add(group);
      this.checkpointMeshes.push(group);

      // Store reference to ring for highlighting
      cp.ringMesh = ring;
    }

    this.highlightCurrentCheckpoint();
  }

  private clearCheckpointMeshes(): void {
    for (const mesh of this.checkpointMeshes) {
      this.context.scene.remove(mesh);
    }
    this.checkpointMeshes = [];
  }

  private highlightCurrentCheckpoint(): void {
    const track = this.tracks[this.currentTrackIndex];

    for (let i = 0; i < track.checkpoints.length; i++) {
      const cp = track.checkpoints[i];
      if (cp.ringMesh) {
        const mat = cp.ringMesh.material as THREE.MeshStandardMaterial;
        if (i === this.currentCheckpoint) {
          mat.emissiveIntensity = 1.5;
          mat.opacity = 1;
        } else if (i < this.currentCheckpoint) {
          mat.emissiveIntensity = 0.1;
          mat.opacity = 0.3;
        } else {
          mat.emissiveIntensity = 0.3;
          mat.opacity = 0.6;
        }
      }
    }
  }

  public update(dt: number): void {
    if (!this.isActive) return;

    // Handle countdown
    if (this.countdownTimer > 0) {
      this.countdownTimer -= dt;
      const count = Math.ceil(this.countdownTimer);

      if (count <= 0 && !this.isRacing) {
        this.startRace();
        this.hud.showCountdown(0); // "GO!"
      } else {
        this.hud.showCountdown(count);
      }
      return;
    }

    if (!this.isRacing || this.isFinished) return;

    // Update timing
    const currentTime = performance.now();
    const raceTime = (currentTime - this.raceStartTime) / 1000;
    const lapTime = (currentTime - this.lapStartTime) / 1000;

    this.hud.updateTime(raceTime, lapTime, this.currentLap);

    // Check checkpoint collision
    this.checkCheckpoints();

    // Animate checkpoint rings
    this.animateCheckpoints(dt);
  }

  private startRace(): void {
    this.isRacing = true;
    this.raceStartTime = performance.now();
    this.lapStartTime = this.raceStartTime;
    this.currentCheckpoint = 0;
    this.currentLap = 1;
    this.lapTimes = [];

    EventBus.emit('score:added', {
      points: 0,
      reason: 'race_start',
    });
  }

  private checkCheckpoints(): void {
    const track = this.tracks[this.currentTrackIndex];
    const playerPos = this.context.getPlayerPosition();
    const cp = track.checkpoints[this.currentCheckpoint];

    const distance = playerPos.distanceTo(cp.position);

    if (distance < cp.radius + 2) {
      this.onCheckpointReached();
    }
  }

  private onCheckpointReached(): void {
    const track = this.tracks[this.currentTrackIndex];
    const currentTime = performance.now();

    this.currentCheckpoint++;

    // Play checkpoint sound
    EventBus.emit('score:added', {
      points: 50,
      reason: 'checkpoint',
    });

    // Check if lap complete
    if (this.currentCheckpoint >= track.checkpoints.length) {
      this.currentCheckpoint = 0;
      const lapTime = (currentTime - this.lapStartTime) / 1000;
      this.lapTimes.push(lapTime);

      this.hud.showLapTime(lapTime, this.bestLapTime);

      // Update best lap
      if (this.bestLapTime === null || lapTime < this.bestLapTime) {
        this.bestLapTime = lapTime;
      }

      this.currentLap++;
      this.lapStartTime = currentTime;

      // Check if race complete
      if (this.currentLap > track.laps) {
        this.onRaceComplete();
        return;
      }

      this.hud.updateLap(this.currentLap, track.laps);
    }

    this.highlightCurrentCheckpoint();
  }

  private onRaceComplete(): void {
    this.isFinished = true;
    this.isRacing = false;

    const totalTime = (performance.now() - this.raceStartTime) / 1000;
    const track = this.tracks[this.currentTrackIndex];

    // Check for new record
    const isNewRecord = track.bestTime === null || totalTime < track.bestTime;

    if (isNewRecord) {
      track.bestTime = totalTime;
      localStorage.setItem(
        `timeTrial_${this.currentTrackIndex}_bestTime`,
        totalTime.toString()
      );
    }

    // Calculate time bonus
    const timeBonus = Math.max(0, Math.floor((300 - totalTime) * 10));
    EventBus.emit('score:added', {
      points: 1000 + timeBonus,
      reason: 'race_complete',
    });

    this.hud.showRaceComplete(totalTime, this.lapTimes, track.bestTime, isNewRecord);
  }

  private animateCheckpoints(_dt: number): void {
    const track = this.tracks[this.currentTrackIndex];
    const time = performance.now() / 1000;

    // Pulse the current checkpoint
    const currentCp = track.checkpoints[this.currentCheckpoint];
    if (currentCp.ringMesh) {
      const scale = 1 + Math.sin(time * 3) * 0.1;
      currentCp.ringMesh.scale.set(scale, scale, scale);
    }
  }

  public restartRace(): void {
    this.selectTrack(this.currentTrackIndex);
  }

  public nextTrack(): void {
    const nextIndex = (this.currentTrackIndex + 1) % this.tracks.length;
    this.selectTrack(nextIndex);
  }

  public previousTrack(): void {
    const prevIndex = (this.currentTrackIndex - 1 + this.tracks.length) % this.tracks.length;
    this.selectTrack(prevIndex);
  }

  public getTracks(): Track[] {
    return this.tracks;
  }

  public getCurrentTrack(): Track {
    return this.tracks[this.currentTrackIndex];
  }

  public isRaceFinished(): boolean {
    return this.isFinished;
  }
}
